/**
 * P3-05: 中栏共存容器分区——React 分区与旧 dojo 中栏的显隐切换。
 *
 * 结构（#midColumn 内，index.html）已随 P5-08 移除 #midColumnViewport：
 *   #gameContainerId     —— 旧 dojo 中栏：DesktopUI.render() 每帧
 *                          dojo.empty 重建 .tabsContainer + .tabInner（js/ui.js:384-450）
 *   #kgMidPanelViewport  —— 新 React 分区（本模块创建，#gameContainerId 的
 *                          **兄弟节点**，必在其影响半径之外；初始 display:none）
 *
 * 显隐规则（数据驱动，单一条件）：激活 tab 已迁移（MIGRATED_TABS 注册表）
 * → #gameContainerId 整棵隐藏（含旧 .tabsContainer/.tabInner）、React 分区
 * 显示（TabNav + 该 tab 的 React 内容）；未迁移 → 相反。两个区域永不同时显示，
 * 与旧版"同一容器内单 tab 渲染"语义等价。
 *
 * 旧路径保留：dojo render 循环原样运行（隐藏时仍重建 DOM，零逻辑改动），
 * 各 tab 迁移完成后才随 P3-46 退役。
 *
 * 滚动：旧 render() 保存/恢复 midColumn.scrollTop（js/ui.js:374-375）；
 * React 分区切换不写 scrollTop，dojo 路径行为不变。
 *
 * 挂载入口：KGNewUI.mountMidPanel()（main.tsx）；本阶段**未启用**——
 * index.html 不调用，注册表为空，页面行为与现状零差异。
 */
import type { ComponentType } from "react";
import { DOJO_EMPTY_CONTAINER_ID } from "../bridge/GameBridge";

export const MID_PANEL_VIEWPORT_ID = "kgMidPanelViewport";

type MidTabComponent = ComponentType<Record<string, unknown>>;

/**
 * 已迁移 tab 注册表：tabId → React 组件。
 * 各 tab 迁移步骤（§3.1~3.8）在切换时登记；本阶段为空。
 */
const MIGRATED_TABS = new Map<string, MidTabComponent>();

export function registerMigratedTab(tabId: string, component: MidTabComponent): void {
  MIGRATED_TABS.set(tabId, component);
}

export function isTabMigrated(tabId: string): boolean {
  return MIGRATED_TABS.has(tabId);
}

export function getMigratedTabComponent(tabId: string): MidTabComponent | null {
  return MIGRATED_TABS.get(tabId) ?? null;
}

/** 测试专用：清空注册表 */
export function clearMigratedTabs(): void {
  MIGRATED_TABS.clear();
}

/**
 * 取（或创建）React 分区挂载点：#gameContainerId 的兄弟节点，紧随其后。
 * 初始 display:none；容器不存在（游戏未初始化）时返回 null。
 */
export function ensureMidPanelViewport(): HTMLElement | null {
  const container = document.getElementById(DOJO_EMPTY_CONTAINER_ID);
  if (!container || !container.parentElement) {
    return null;
  }
  let viewport = document.getElementById(MID_PANEL_VIEWPORT_ID) as HTMLElement | null;
  if (!viewport) {
    viewport = document.createElement("div");
    viewport.id = MID_PANEL_VIEWPORT_ID;
    viewport.style.display = "none";
    container.parentElement.insertBefore(viewport, container.nextSibling);
  }
  return viewport;
}

/**
 * 按激活 tab 的迁移状态切换两个区域显隐（MidPanel 每 tick 驱动）。
 * 幂等：重复调用写相同值。
 */
export function syncMidPanelVisibility(activeTabId: string | null): void {
  const container = document.getElementById(DOJO_EMPTY_CONTAINER_ID) as HTMLElement | null;
  const viewport = document.getElementById(MID_PANEL_VIEWPORT_ID) as HTMLElement | null;
  if (!container || !viewport) {
    return;
  }
  const migrated = activeTabId !== null && isTabMigrated(activeTabId);
  container.style.display = migrated ? "none" : "";
  viewport.style.display = migrated ? "" : "none";
}

/** MidPanel 卸载时恢复：dojo 中栏重新可见、React 分区隐藏。 */
export function restoreMidPanelVisibility(): void {
  const container = document.getElementById(DOJO_EMPTY_CONTAINER_ID) as HTMLElement | null;
  const viewport = document.getElementById(MID_PANEL_VIEWPORT_ID) as HTMLElement | null;
  if (container) {
    container.style.display = "";
  }
  if (viewport) {
    viewport.style.display = "none";
  }
}
